import { KeyedSet } from "../support/index.js";
export class CertifiedClosurePhase {
    rootBranchId;
    graph;
    nextSplitId = 1;
    nextBranchId = 1;
    nextBranchGroupId = 1;
    closedBoundary;
    closedBoundaryDomainHint;
    branches = new KeyedSet((value) => value.id);
    splits = new KeyedSet((value) => value.id);
    reachedByBranch = new Map();
    terminalNodes = new Set();
    constructor(graph, startId, telemetry) {
        this.graph = new ClosureGraphState(graph, telemetry);
        this.rootBranchId = 0;
        this.graph.markCoveredByClosedRegion(startId);
        this.branches.add({
            id: this.rootBranchId,
            splitId: 0,
            startedAt: startId,
            groupId: 0,
        });
        this.recordBranchReachAndDetectJoin(startId, this.rootBranchId);
    }
    async begin(rootDomainHint) {
        const rootItem = {
            nodeId: this.getBranchStateOrThrow(this.rootBranchId).startedAt,
            branchId: this.rootBranchId,
            ...(rootDomainHint === undefined ? {} : { domainHint: rootDomainHint }),
        };
        const successors = await this.graph.expand(rootItem.nodeId);
        this.graph.recordBranchTraversal(rootItem.nodeId, rootItem.branchId);
        if (successors.length === 0) {
            this.markTerminal(rootItem.nodeId);
            return [];
        }
        if (successors.length === 1) {
            const successor = successors[0];
            if (successor === undefined)
                throw new Error("Expected single successor.");
            this.graph.recordTraversedEdge(rootItem.nodeId, successor.nodeId);
            this.recordBranchReachAndDetectJoin(successor.nodeId, rootItem.branchId);
            this.graph.markCoveredByClosedRegion(successor.nodeId);
            this.closedBoundary = successor.nodeId;
            this.closedBoundaryDomainHint = successor.domainHint;
            return [];
        }
        return this.processSplitSuccessors(rootItem, successors);
    }
    async processFrontierItem(item) {
        const state = this.graph.getNodeStateOrThrow(item.nodeId);
        if (state.traversedBranches.has(item.branchId)) {
            this.graph.recordStaleStep();
            return [];
        }
        // A re-expanded node may be reached by another branch, but new branch joins are detected when
        // the successor frontier item is produced. Dequeue-time re-expansion is topology re-access only.
        const frontier = [];
        const successors = await this.graph.expand(item.nodeId);
        this.graph.recordBranchTraversal(item.nodeId, item.branchId);
        if (successors.length === 0) {
            this.markTerminal(item.nodeId);
            return frontier;
        }
        if (successors.length === 1) {
            const successor = successors[0];
            if (successor === undefined)
                throw new Error("Expected single successor.");
            frontier.push(...this.processSingleSuccessor(item, successor));
            return frontier;
        }
        frontier.push(...this.processSplitSuccessors(item, successors));
        return frontier;
    }
    hasClosedBoundary() {
        return this.closedBoundary !== undefined;
    }
    processSingleSuccessor(item, successor) {
        this.graph.recordTraversedEdge(item.nodeId, successor.nodeId);
        const hit = this.advanceBranchToSuccessorAndDetectJoin(item.branchId, successor.nodeId, successor.domainHint);
        const frontier = hit === undefined ? [] : this.applyBranchJoinTrigger(hit);
        if (!this.graph.getNodeStateOrThrow(successor.nodeId).expanded) {
            frontier.push({
                nodeId: successor.nodeId,
                branchId: item.branchId,
                ...(successor.domainHint === undefined ? {} : { domainHint: successor.domainHint }),
            });
        }
        return frontier;
    }
    processSplitSuccessors(item, successors) {
        const frontier = [];
        const childSplit = this.openSplit(item.nodeId, item.branchId, successors.map((successor) => successor.nodeId));
        for (const branchId of childSplit.branchIds) {
            const branch = this.getBranchStateOrThrow(branchId);
            this.graph.recordTraversedEdge(item.nodeId, branch.startedAt);
            const successor = successors.find((candidate) => candidate.nodeId === branch.startedAt);
            const hit = this.advanceBranchToSuccessorAndDetectJoin(branch.id, branch.startedAt, successor?.domainHint);
            if (hit !== undefined)
                frontier.push(...this.applyBranchJoinTrigger(hit));
            if (!this.graph.getNodeStateOrThrow(branch.startedAt).expanded) {
                frontier.push({
                    nodeId: branch.startedAt,
                    branchId: branch.id,
                    ...(successor?.domainHint === undefined ? {} : { domainHint: successor.domainHint }),
                });
            }
        }
        return frontier;
    }
    openSplit(openedAt, openedFromBranchId, successors) {
        const splitId = this.nextSplitId++;
        const branchIds = successors.map((successor) => {
            const branchId = this.nextBranchId++;
            this.branches.add({
                id: branchId,
                splitId,
                startedAt: successor,
                groupId: this.nextBranchGroupId++,
            });
            return branchId;
        });
        const split = {
            id: splitId,
            openedAt,
            openedFromBranchId,
            branchIds,
            triggers: new Set(),
            status: "open",
        };
        this.splits.add(split);
        return split;
    }
    advanceBranchToSuccessorAndDetectJoin(branchId, successorId, domainHint) {
        const branch = this.getBranchStateOrThrow(branchId);
        const join = this.recordBranchReachAndDetectJoin(successorId, branchId);
        if (join.kind === "no-join")
            return undefined;
        return {
            splitId: branch.splitId,
            triggerId: successorId,
            branchId,
            joinedBranchId: join.joinedBranchId,
            domainHint,
        };
    }
    applyBranchJoinTrigger(hit) {
        const split = this.getSplitStateOrThrow(hit.splitId);
        if (split.status === "closed")
            return [];
        split.triggers.add(hit.triggerId);
        this.mergeBranchGroups(hit.branchId, hit.joinedBranchId);
        const boundary = this.findCloseBoundary(split);
        if (boundary === undefined)
            return [];
        return this.closeSplitAndPropagate(split, boundary, hit.domainHint);
    }
    getBranchStateOrThrow(branchId) {
        const branch = this.branches.getByKey(branchId);
        if (branch === undefined)
            throw new Error(`Unknown branch: ${branchId}`);
        return branch;
    }
    markTerminal(nodeId) {
        this.terminalNodes.add(nodeId);
    }
    closeSplitAndPropagate(split, boundary, domainHint) {
        split.status = "closed";
        this.markClosedRegion(split.openedAt, boundary);
        const parentBranch = this.getBranchStateOrThrow(split.openedFromBranchId);
        if (parentBranch.splitId === 0) {
            this.closedBoundary = boundary;
            this.closedBoundaryDomainHint = domainHint;
            return [];
        }
        const join = this.recordBranchReachAndDetectJoin(boundary, parentBranch.id);
        if (join.kind === "join-detected") {
            const parentFrontier = this.applyBranchJoinTrigger({
                splitId: parentBranch.splitId,
                triggerId: boundary,
                branchId: parentBranch.id,
                joinedBranchId: join.joinedBranchId,
                domainHint,
            });
            if (parentFrontier.length > 0 || this.hasClosedBoundary())
                return parentFrontier;
        }
        return [
            {
                nodeId: boundary,
                branchId: parentBranch.id,
                ...(domainHint === undefined ? {} : { domainHint }),
            },
        ];
    }
    buildResolution() {
        if (this.closedBoundary !== undefined) {
            return {
                result: {
                    kind: "closed-boundary",
                    certifiedNodes: new Set([...this.graph.nodeStates()]
                        .filter((state) => state.closedCover)
                        .map((state) => state.nodeId)),
                    closedBoundary: this.closedBoundary,
                },
                closedBoundaryDomainHint: this.closedBoundaryDomainHint,
            };
        }
        return {
            result: {
                kind: "exhausted",
                certifiedNodes: new Set([...this.graph.nodeStates()]
                    .filter((state) => state.reached)
                    .map((state) => state.nodeId)),
                terminalNodes: [...this.terminalNodes],
            },
        };
    }
    recordBranchReachAndDetectJoin(nodeId, branchId) {
        this.graph.markReached(nodeId);
        let reached = this.reachedByBranch.get(nodeId);
        if (reached === undefined) {
            reached = new Set();
            this.reachedByBranch.set(nodeId, reached);
        }
        reached.add(branchId);
        const currentBranch = this.getBranchStateOrThrow(branchId);
        const splitId = currentBranch.splitId;
        const joinedBranchId = [...reached].find((candidate) => {
            const branch = this.getBranchStateOrThrow(candidate);
            return (branch.splitId === splitId &&
                candidate !== branchId &&
                branch.groupId !== currentBranch.groupId);
        });
        // Reach registration and join detection complete together before successor work is enqueued.
        return joinedBranchId === undefined
            ? { kind: "no-join" }
            : { kind: "join-detected", joinedBranchId };
    }
    mergeBranchGroups(leftBranchId, rightBranchId) {
        const left = this.getBranchStateOrThrow(leftBranchId);
        const right = this.getBranchStateOrThrow(rightBranchId);
        if (left.groupId === right.groupId)
            return;
        const from = right.groupId;
        const to = left.groupId;
        // Branch groups only merge. Reassigning the whole matching group preserves earlier joins and
        // prevents later traversal order from splitting a resolved relationship apart.
        for (const branch of this.branches.values()) {
            if (branch.splitId === left.splitId && branch.groupId === from) {
                branch.groupId = to;
            }
        }
    }
    findCloseBoundary(split) {
        const groupIds = new Set(split.branchIds.map((branchId) => this.getBranchStateOrThrow(branchId).groupId));
        if (groupIds.size !== 1)
            return undefined;
        const dominated = new Set();
        for (const trigger of split.triggers) {
            for (const seen of this.walkPredecessorsUntil(trigger, split.openedAt)) {
                if (seen !== trigger && split.triggers.has(seen))
                    dominated.add(seen);
            }
        }
        const candidates = [...split.triggers].filter((trigger) => !dominated.has(trigger));
        return candidates.length === 1 ? candidates[0] : undefined;
    }
    *walkPredecessorsUntil(from, stop) {
        yield* this.graph.walkPredecessorsUntil(from, stop);
    }
    markClosedRegion(openedAt, boundary) {
        for (const nodeId of this.walkPredecessorsUntil(boundary, openedAt)) {
            this.graph.markCoveredByClosedRegion(nodeId);
        }
    }
    getSplitStateOrThrow(splitId) {
        const split = this.splits.getByKey(splitId);
        if (split === undefined)
            throw new Error(`Unknown split: ${splitId}`);
        return split;
    }
}
/**
 * Stores the closure phase's explored DAG view.
 *
 * Unlike `IncludeGraphState`, this graph does not record every edge immediately when a node is
 * expanded. The closure phase decides when an edge has become part of a branch traversal, then calls
 * `recordTraversedEdge()`. The graph only needs predecessor links for walking back through the
 * certified closed region.
 */
class ClosureGraphState {
    graph;
    telemetry;
    visited = new Map();
    constructor(graph, telemetry) {
        this.graph = graph;
        this.telemetry = telemetry;
    }
    getNodeStateOrThrow(nodeId) {
        const state = this.visited.get(nodeId);
        if (state === undefined)
            throw new Error("Expected reached closure node.");
        return state;
    }
    ensureMutableNodeState(nodeId) {
        let state = this.visited.get(nodeId);
        if (state === undefined) {
            state = {
                nodeId,
                predecessors: new Set(),
                traversedBranches: new Set(),
                reached: false,
                expanded: false,
                closedCover: false,
            };
            this.visited.set(nodeId, state);
        }
        return state;
    }
    async expand(nodeId) {
        this.telemetry?.span.incrementCounter("successor_expansions");
        this.telemetry?.span.incrementCounter("exclude_expansions");
        const successors = await this.graph.getSuccessors(nodeId);
        this.markExpandedAndReached(nodeId);
        return successors;
    }
    markExpandedAndReached(nodeId) {
        const state = this.ensureMutableNodeState(nodeId);
        this.visited.set(nodeId, {
            ...state,
            expanded: true,
            reached: true,
        });
    }
    markReached(nodeId) {
        this.ensureMutableNodeState(nodeId).reached = true;
    }
    markCoveredByClosedRegion(nodeId) {
        this.ensureMutableNodeState(nodeId).closedCover = true;
    }
    recordBranchTraversal(nodeId, branchId) {
        this.ensureMutableNodeState(nodeId).traversedBranches.add(branchId);
    }
    recordTraversedEdge(nodeId, successorId) {
        this.ensureMutableNodeState(successorId).predecessors.add(nodeId);
    }
    recordStaleStep() {
        this.telemetry?.span.incrementCounter("stale_steps");
    }
    nodeStates() {
        return this.visited.values();
    }
    *walkPredecessorsUntil(from, stop) {
        const stack = [from];
        const seen = new Set();
        while (stack.length > 0) {
            const nodeId = stack.pop();
            if (nodeId === undefined || seen.has(nodeId))
                continue;
            seen.add(nodeId);
            yield nodeId;
            if (nodeId === stop)
                continue;
            // These links reconstruct the certified closed region; they are correctness state, not a
            // successor cache. Every walked node must already have been reached by a closure branch.
            for (const predecessor of this.getNodeStateOrThrow(nodeId).predecessors) {
                stack.push(predecessor);
            }
        }
    }
}
//# sourceMappingURL=certified-closure.js.map